import { Activity, CheckCircle2, MinusCircle, XCircle } from 'lucide-react'
import { useState } from 'react'
import { GenerateInsightsButton } from '@/components/ai-insights/GenerateInsightsButton'
import { InsightCard } from '@/components/ai-insights/InsightCard'
import { SpectreThinkingBadge } from '@/components/ai-insights/SpectreThinkingBadge'
import { Card } from '@/components/ui/card'
import type { Lens } from '@/features/ai-insights'
import { useAiInsights } from '@/features/ai-insights/hooks/useAiInsights'
import type { InsightRunSummary, RunStatus } from '@/features/ai-insights/types'
import { useInsightHistory } from '@/features/cases/hooks/useInsightHistory'
import { useCaseContext } from '../../CaseContext'

const LENSES: { value: Lens; label: string; phase1: boolean }[] = [
  { value: 'graph', label: 'Graph', phase1: true },
  { value: 'geo_static', label: 'Geo (static)', phase1: false },
  { value: 'geo_dynamic', label: 'Geo (dynamic)', phase1: false },
  { value: 'timeline', label: 'Timeline', phase1: false },
]

const STATUS_TABS: { value: 'active' | 'accepted' | 'skipped'; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'skipped', label: 'Skipped' },
]

/**
 * Full-page AI insights view — not linked from the icon rail. Reachable
 * by URL only (`/cases/:caseId/ai-insights`). Useful for QA, dev work, and
 * browsing accepted/skipped history that the sidebar tab doesn't expose.
 *
 * Generate is always allowed — the backend has no concurrency cap; users
 * can stack runs and watch them in the runs panel below.
 */
export default function AiInsightsPage() {
  const { caseId } = useCaseContext()
  const [lens, setLens] = useState<Lens>('graph')
  const [tab, setTab] = useState<'active' | 'accepted' | 'skipped'>('active')

  const live = useAiInsights(caseId, lens)

  const { history, loading: historyLoading } = useInsightHistory(
    caseId,
    lens,
    tab === 'active' ? 'accepted' : tab,
    tab !== 'active'
  )

  const visible = tab === 'active' ? live.insights : history
  const loading = tab === 'active' ? live.loading : historyLoading

  return (
    <div className="mx-auto flex h-full w-full max-w-5xl flex-col gap-6 p-4 max-lg:overflow-y-auto md:p-6">
      <header className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-foreground">AI Insights</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Narrative findings produced by Xendrel's insights agent. Phase 1 surfaces the graph
            lens.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <SpectreThinkingBadge visible={live.refreshing} />
          <GenerateInsightsButton
            onClick={live.refresh}
            disabled={lens !== 'graph'}
            emphasis="primary"
          />
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-3xs font-semibold uppercase tracking-widest text-muted-foreground/70">
          Lens
        </span>
        {LENSES.map(l => (
          <button
            key={l.value}
            type="button"
            onClick={() => setLens(l.value)}
            disabled={!l.phase1}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 max-md:py-2 ${
              lens === l.value
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground'
            }`}
            title={l.phase1 ? l.label : `${l.label} — phase 2`}
          >
            {l.label}
            {!l.phase1 && <span className="ml-1 text-4xs opacity-70">soon</span>}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-1 border-b border-border">
        {STATUS_TABS.map(t => (
          <button
            key={t.value}
            type="button"
            onClick={() => setTab(t.value)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
              tab === t.value
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <section className="flex flex-col gap-3">
        {loading && visible.length === 0 && (
          <p className="py-8 text-center text-xs text-muted-foreground">Loading…</p>
        )}
        {!loading && visible.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-12 text-center">
            <p className="text-sm text-muted-foreground">
              No {tab} insights for the {lens} lens yet.
            </p>
            {tab === 'active' && lens === 'graph' && (
              <GenerateInsightsButton onClick={live.refresh} emphasis="primary" />
            )}
          </div>
        )}
        {visible.map(i => (
          <InsightCard
            key={i.id}
            insight={i}
            onAccept={tab === 'active' ? live.accept : () => {}}
            onSkip={tab === 'active' ? live.skip : () => {}}
          />
        ))}
      </section>

      <RunsPanel runs={live.runs} />
    </div>
  )
}

// ─── Runs panel ────────────────────────────────────────────────────────────

function RunsPanel({ runs }: { runs: InsightRunSummary[] }) {
  if (runs.length === 0) return null
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-3xs font-semibold uppercase tracking-widest text-muted-foreground/70">
        Recent runs ({runs.length})
      </h2>
      <Card asChild size="compact" className="p-0">
        <ul className="divide-y divide-border">
          {runs.map(r => (
            <li
              key={r.run_id}
              className="flex items-center gap-3 px-3 py-2 text-xs max-md:flex-wrap max-md:gap-x-2 max-md:gap-y-1"
              data-status={r.status}
            >
              <RunStatusChip status={r.status} />
              <span className="font-mono text-3xs uppercase tracking-wide text-muted-foreground">
                {r.mode}
              </span>
              <span className="font-mono text-3xs text-muted-foreground">{r.lens}</span>
              <span className="ml-auto text-3xs text-muted-foreground">{formatRunTime(r)}</span>
              <span className="w-12 text-right tabular-nums text-foreground">
                {r.insight_count}
                <span className="ml-1 text-3xs text-muted-foreground">cards</span>
              </span>
              <span className="font-mono text-3xs text-muted-foreground/60" title={r.run_id}>
                {r.run_id.slice(0, 8)}
              </span>
            </li>
          ))}
        </ul>
      </Card>
    </section>
  )
}

function RunStatusChip({ status }: { status: RunStatus }) {
  if (status === 'running') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-3xs font-medium text-primary">
        <Activity className="size-3 animate-pulse" />
        running
      </span>
    )
  }
  if (status === 'done') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-3xs font-medium text-foreground">
        <CheckCircle2 className="size-3" />
        done
      </span>
    )
  }
  if (status === 'aborted') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-3xs font-medium text-muted-foreground">
        <MinusCircle className="size-3" />
        aborted
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-3xs font-medium text-destructive">
      <XCircle className="size-3" />
      failed
    </span>
  )
}

function formatRunTime(r: InsightRunSummary): string {
  const started = new Date(r.started_at)
  if (r.status === 'running') {
    const seconds = Math.max(0, Math.floor((Date.now() - started.getTime()) / 1000))
    return `started ${seconds}s ago`
  }
  if (r.finished_at) {
    const finished = new Date(r.finished_at)
    const ms = finished.getTime() - started.getTime()
    return `${Math.max(0, Math.round(ms / 100) / 10)}s`
  }
  return started.toLocaleTimeString()
}
